import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Flame, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";

const Auth = () => {
  const [mode, setMode] = useState("login"); // login | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginEmail, registerEmail } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await loginEmail(email, password);
      } else {
        await registerEmail(email, password, name);
      }
      toast.success("Welcome to Suitor.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auth failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-10" data-testid="auth-brand">
          <Flame className="w-6 h-6 text-amber-500" />
          <span className="font-serif text-3xl">Suitor</span>
        </Link>

        <div className="card-surface p-8">
          <h1 className="font-serif text-3xl mb-2" data-testid="auth-title">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-neutral-400 text-sm mb-6">
            {mode === "login" ? "Sign in to continue your coaching." : "Start your dating & relationships playbook."}
          </p>

          <button
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors mb-5"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            <span className="text-white font-medium">Continue with Google</span>
          </button>

          <div className="flex items-center gap-4 my-4">
            <div className="flex-1 divider-thin"/>
            <span className="text-xs text-neutral-500">or</span>
            <div className="flex-1 divider-thin"/>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="overline text-neutral-500">Name</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-2 px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/40 transition-colors"
                  data-testid="signup-name-input"
                  placeholder="Your first name"
                />
              </div>
            )}
            <div>
              <label className="overline text-neutral-500">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/40 transition-colors"
                data-testid="auth-email-input"
                placeholder="you@domain.com"
              />
            </div>
            <div>
              <label className="overline text-neutral-500">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mt-2 px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/40 transition-colors"
                data-testid="auth-password-input"
                placeholder="At least 6 characters"
                minLength={6}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-amber w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="auth-submit-btn"
            >
              {loading ? "Please wait…" : (mode === "login" ? "Sign in" : "Create account")}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="text-center text-sm text-neutral-400 mt-6">
            {mode === "login" ? "New to Suitor?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="text-amber-500 hover:underline"
              data-testid="auth-toggle-btn"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>

        <p className="text-center text-xs text-neutral-600 mt-6">
          <Mail className="w-3 h-3 inline mr-1" />
          Free during launch. No card required.
        </p>
      </div>
    </div>
  );
};

export default Auth;
